import { useSyncExternalStore } from 'react'

/**
 * Whether LaunchDarkly is actually configured.
 *
 * With no client id, `main.tsx` still mounts the provider — local feature flags are bootstrapped
 * from `localFeatureFlags.local.ts` and read straight out of that bootstrap — but it mounts under
 * the placeholder `local-dev-client-id`. That placeholder is why `streaming`, `sendEvents` and
 * `fetchGoals` are switched off there: there is nothing to talk to.
 *
 * `identify()` is the one call those switches do not cover. It always re-evaluates flags for the
 * new context over the network, so against a placeholder id LaunchDarkly answers `401` and the
 * SDK rejects — an error with nothing behind it, since the flag values came from the bootstrap
 * and re-evaluating per user means nothing when they come from a local file.
 */
export const hasLdClientId = Boolean(import.meta.env.VITE_LAUNCHDARKLY_CLIENT_ID)

/*
 * LaunchDarkly starts on an anonymous context (main.tsx) and LDUserIdentifier switches it to the
 * signed-in user. Until that switch completes, flags are the anonymous user's — so a per-user
 * flag must not be read as "off" before then. This records which user key has been identified.
 */
let identifiedKey: string | null = null
const listeners = new Set<() => void>()

export function markLdIdentified(key: string): void {
  identifiedKey = key
  for (const listener of listeners) listener()
}

const subscribe = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** The user key LaunchDarkly has finished identifying (null until the first identify settles). */
export function useLdIdentifiedKey(): string | null {
  return useSyncExternalStore(subscribe, () => identifiedKey)
}
